import { toast } from "sonner";
import { useState, useContext, useEffect } from "react";
import { ErpProvider, SimiFormData, NuwweFormData, ERP_PROVIDERS } from "./types";
import ErpProviderSelector from "./ErpProviderSelector";
import SimiErpForm from "./simi/SimiErpForm";
import NuwweErpForm from "./nuwwe/NuwweErpForm";
import { trpc } from "../../trpc";
import MerchantContext from "../../contexts/MerchantContext";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";

const ErpConfiguration = (): JSX.Element => {
  const { merchantId } = useContext(MerchantContext);
  const {
    data: merchant,
    isLoading,
  } = trpc.merchants.get.useQuery({ id: merchantId || "" }, { enabled: !!merchantId });

  const [selectedProvider, setSelectedProvider] = useState<ErpProvider | null>(null);
  const [simiFormData, setSimiFormData] = useState<SimiFormData>({
    usernameAdmin: "",
    passwordAdmin: "",
  });
  const [nuwweFormData, setNuwweFormData] = useState<NuwweFormData>({
    merchantUrl: "",
    token: "",
    empresa: "",
  });

  const utils = trpc.useUtils();

  // Pre-fill non-sensitive fields when merchant data loads
  useEffect(() => {
    if (!merchant) return;

    if (merchant.erp) {
      setSelectedProvider(merchant.erp);
    }

    if (merchant.nuwwe) {
      setNuwweFormData((prev) => ({
        ...prev,
        merchantUrl: merchant.nuwwe?.merchantUrl || "",
        empresa: merchant.nuwwe?.empresa || "",
      }));
    }
  }, [merchant?.erp, merchant?.nuwwe?.merchantUrl, merchant?.nuwwe?.empresa]);

  const saveErpConfig = trpc.merchants.saveErpConfig.useMutation({
    onSuccess: async () => {
      await utils.merchants.get.invalidate({ id: merchantId || "" });
      toast.success("Configuracion ERP guardada exitosamente");
    },
    onError: (error) => {
      toast.error("Error al guardar configuracion ERP", {
        description: error.message,
      });
    },
  });

  const handleSaveSimi = () => {
    saveErpConfig.mutate({
      merchantId: merchantId || "",
      provider: "simi",
      usernameAdmin: simiFormData.usernameAdmin || undefined,
      passwordAdmin: simiFormData.passwordAdmin || undefined,
    });
  };

  const handleSaveNuwwe = () => {
    saveErpConfig.mutate({
      merchantId: merchantId || "",
      provider: "nuwwe",
      merchantUrl: nuwweFormData.merchantUrl,
      token: nuwweFormData.token,
      empresa: nuwweFormData.empresa,
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <div>
          <h2 className="text-2xl font-bold mb-2">Configuracion ERP</h2>
          <p className="text-sm text-gray-600">Cargando...</p>
        </div>
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      </div>
    );
  }

  const hasErpConfigured = !!merchant?.erp;
  const configuredProviderName = ERP_PROVIDERS.find((p) => p.id === merchant?.erp)?.name;
  const activeProvider = hasErpConfigured ? merchant?.erp : selectedProvider;

  return (
    <div className="flex flex-col gap-6">
      {/* Title and Subtitle */}
      <div>
        <h2 className="text-2xl font-bold mb-2">Configuracion ERP</h2>
        <p className="text-sm text-gray-600">
          {hasErpConfigured
            ? "Gestiona tu conexion ERP"
            : "Conecta tu ERP en pocos pasos"}
        </p>
      </div>

      {/* ERP Selector - Only show if no connection exists */}
      {!hasErpConfigured && (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div>
            <Label className="text-sm font-semibold text-gray-700 mb-3 block">
              Selecciona ERP
            </Label>
            <ErpProviderSelector
              options={ERP_PROVIDERS}
              selectedOption={selectedProvider}
              onSelect={setSelectedProvider}
              placeholder="Selecciona un ERP"
            />
          </div>
        </div>
      )}

      {/* Show selected ERP when configured */}
      {hasErpConfigured && (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div className="flex flex-col gap-4">
            <div>
              <Label className="text-lg font-bold text-gray-700 mb-3 block">
                ERP Seleccionado
              </Label>
              <p className="text-base font-medium text-gray-700 mb-2">
                {configuredProviderName}
              </p>
              <p className="text-sm text-green-600">Activo</p>
            </div>
          </div>
        </div>
      )}

      {/* Credential forms - always shown when provider selected */}
      {activeProvider === "simi" && (
        <SimiErpForm
          formData={simiFormData}
          onFormDataChange={(data) => setSimiFormData((prev) => ({ ...prev, ...data }))}
          existingConfig={
            merchant?.simi
              ? {
                  hasAdminCredentials: merchant.simi.hasAdminCredentials ?? false,
                }
              : null
          }
          isLoading={saveErpConfig.isLoading}
          onSave={handleSaveSimi}
        />
      )}

      {activeProvider === "nuwwe" && (
        <NuwweErpForm
          formData={nuwweFormData}
          onFormDataChange={(data) => setNuwweFormData((prev) => ({ ...prev, ...data }))}
          existingConfig={
            merchant?.nuwwe
              ? {
                  merchantUrl: merchant.nuwwe.merchantUrl,
                  empresa: merchant.nuwwe.empresa,
                  hasToken: merchant.nuwwe.hasToken ?? false,
                }
              : null
          }
          isLoading={saveErpConfig.isLoading}
          onSave={handleSaveNuwwe}
        />
      )}
    </div>
  );
};

export default ErpConfiguration;
